import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { Stack } from "@mui/material";
import dayjs from "dayjs";
import { safePosthog } from "../utils/conditionalAnalytics";
import React, { useState } from "react";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import { ImageResizable } from "../components/elements/ImageResizeable";
import { useDidMountEffect } from "../customHooks/useDidMountEffect.js";
import { useGlobalState } from "../GlobalStates";
import { dateRangeFormattingShortYear, earlyBirdSlotsRemaining } from "../utils/utils.js";
import "./TinyRowTrip.scss";
import { TinyTripTag } from "./TinyTripTag";
import { TinyTripReview } from "./TinyTripReview";
import { TinyTripFriend } from "./TinyTripFriend";
import StarIcon from "@mui/icons-material/Star";

import relativeTime from "dayjs/plugin/relativeTime";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../utils/currency.js";
import { clubDate, clubTime } from "../utils/clubDates.js";
import { getSofestTripPath } from "../utils/sofestTrips.js";
import { cardLinkProps } from "../utils/appShell";
import { handOffTripLink } from "../utils/astroTrips";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useShouldShowVideo } from "../customHooks/useShouldShowVideo";

dayjs.extend(relativeTime);

let website_url = window.location.origin;

export const TinyTrip = ({
  leader_requirement,
  projectView = true,
  min_price,
  trip_id,
  project_id,
  name,
  age_restriction,
  duration,
  start_date,
  start_time,
  end_date,
  city,
  country,
  meeting_point,
  num_slots_paid,
  total_slots,
  main_images,
  price,
  currency,
  skill_level,
  three_user_images,
  status,
  booking_open_date,
  trip_type,
  main_video,
  autoPlayTripId,
  setAutoPlayTripId,
  early_bird_percentage,
  late_bird_percentage,
  highlights,
  free_reserve_allowed,
  newLaunch,
  rowTitle,
  onTripClick = null,
  allow_public_trip_creation,
  is_dateless,
  view_participants_in_app,

  leader_incentive_max,
  // To display review row
  userReview = null,
  num_nights,
  soclub_weekly_type,
  broad_category,
  total_review_count,
  total_review_score,

  // To display social row
  friend_id,
  friend_image,
  friend_name,
  friend_book_date,
}) => {
  const shouldShowVideo = useShouldShowVideo({ requireWifi: false });

  const { t } = useTranslation();
  const durationLabel = soclub_weekly_type
    ? `${num_nights} ${Number(num_nights) === 1 ? "week" : "weeks"}`
    : t("trip_details_duration", { days: num_nights + 1, nights: num_nights });
  const isEvent = trip_type === "event";
  const daysUntilEvent = dayjs(start_date).startOf("day").diff(dayjs().startOf("day"), "day");
  const eventDateLabel =
    daysUntilEvent >= 0 && daysUntilEvent <= 6
      ? clubDate(start_date, "weekday")
      : dateRangeFormattingShortYear(start_date, end_date);

  const [user, _] = useGlobalState("user");
  const navigate = useNavigate();

  const [promotion, setPromotion] = useGlobalState("promotion");
  const special_discount_name =
    promotion?.discountedTripIdToPromoObject[trip_id]?.special_discount_name;
  const special_discount_percentage =
    promotion?.discountedTripIdToPromoObject[trip_id]?.special_discount_percentage;
  const special_discount_end_date =
    promotion?.discountedTripIdToPromoObject[trip_id]?.special_discount_end_date;

  const isInApp = window.isInApp;

  const [notify, setNotify] = useState(false);

  const [progress, setProgress] = useState((num_slots_paid / total_slots) * 100);

  const handleShowTripDetails = (e) => {
    // capture posthog event
    safePosthog.capture(`home_display_row_clicked`, {
      distinct_id: user?.id,
      rowTitle: rowTitle,
      trip_id: trip_id,
    });

    // If custom onTripClick handler is provided, use it instead of default navigation
    if (onTripClick) {
      const tripData = {
        project_id,
        name,
        city,
        country,
        duration,
        skill_level,
        main_images,
        main_video,
        total_review_count,
        total_review_score,
      };
      onTripClick(tripData);
      return;
    }

    // Default navigation behavior
    const sofestPath = getSofestTripPath(project_id);
    if (sofestPath) {
      if (isMobile) {
        navigate(sofestPath);
      } else {
        window.open(`${website_url}${sofestPath}`, "_blank");
      }
      return;
    }
    if (isMobile) {
      // Projects the Astro app renders load natively (utils/astroTrips.js).
      if (handOffTripLink(`/trips/${project_id}`, project_id, { broad_category })) return;
      navigate(`/trips/${project_id}`, {
        state: {
          main_images: main_images,
          main_video: main_video,
          name: name,
          city: city,
          country: country,
          duration: duration,
          skill_level: skill_level,
          id: project_id,
          broad_category: broad_category,
          total_review_count: total_review_count,
          total_review_score: total_review_score,
        },
      });
    } else {
      window.open(`${website_url}/trips/${project_id}`, "_blank");
    }
  };

  const playVideoBox = () => {
    return (
      <div
        className="tw-absolute tw-bottom-2 tw-right-2 tw-z-10"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          const sofestPath = getSofestTripPath(project_id);
          if (sofestPath) {
            navigate(sofestPath);
            return;
          }
          if (handOffTripLink(`/trips/${project_id}`, project_id, { broad_category })) return;
          navigate(`/trips/${project_id}`, {
            state: {
              main_images: main_images,
              main_video: main_video,
              name: name,
              city: city,
              country: country,
              duration: duration,
              skill_level: skill_level,
              id: project_id,
              broad_category: broad_category,
              playVideoWithSound: true,
              total_review_count: total_review_count,
              total_review_score: total_review_score,
            },
          });
        }}
      >
        <div className="tw-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-white/80 tw-p-1.5 tw-shadow-md tw-backdrop-blur-sm">
          <PlayArrowIcon style={{ fontSize: "24px", color: "#333" }} />
          <span className="tw-pr-1.5 tw-text-xs tw-font-bold tw-text-gray-800">
            {t("play_video")}
          </span>
        </div>
      </div>
    );
  };
  const earlyBirdRemainingSlots = earlyBirdSlotsRemaining(
    num_slots_paid,
    total_slots,
    early_bird_percentage,
    late_bird_percentage
  );

  return (
    <div className="TinyTripWrapper">
      {/* A real link so crawlers can follow the card; clicks keep handleShowTripDetails. */}
      <a
        className="TinyTripContainer"
        {...cardLinkProps(
          getSofestTripPath(project_id) || `/trips/${project_id}`,
          handleShowTripDetails
        )}
      >
        <div className="TinyTripImageBox">
          <ImageResizable
            size={isMobile ? "l" : "xl"}
            src={main_images?.length > 0 ? main_images[0] : null}
            className="TinyTripImage"
            alt="trip"
            fadeIn={true}
          />

          <TinyTripTag
            status={status}
            num_slots_paid={num_slots_paid}
            total_slots={total_slots}
            special_discount_name={special_discount_name}
            special_discount_percentage={special_discount_percentage}
            special_discount_end_date={special_discount_end_date}
            price={price}
            newLaunch={newLaunch}
            trip_type={trip_type}
            early_bird_percentage={early_bird_percentage}
            free_reserve_allowed={free_reserve_allowed}
            user={user}
            start_date={start_date}
            is_dateless={is_dateless}
            isInApp={isInApp}
            is_leader={user?.host_type && user.host_type !== "Duckling"}
            leader_requirement={leader_requirement}
            earlyBirdRemainingSlots={earlyBirdRemainingSlots}
            leader_incentive_max={leader_incentive_max}
            project_id={project_id}
            projectView={projectView}
            name={name}
            broad_category={broad_category}
            view_participants_in_app={view_participants_in_app}
          />

          {!friend_id && !userReview && (
            <div className="TinyTripGroupDetailsContainer">
              <div className="TinyTripGroupDetailsImages">
                {three_user_images &&
                  three_user_images.map((imageUrl, index) => (
                    <ImageResizable
                      key={index}
                      className="TinyTripDetailsGroupMembersProfile"
                      src={imageUrl}
                      style={
                        index === 1
                          ? { marginLeft: "1rem" }
                          : index === 2
                            ? {
                                marginLeft: "2rem",
                              }
                            : {}
                      }
                      size="xs"
                    />
                  ))}
              </div>
            </div>
          )}
          <TinyTripFriend
            friend_id={friend_id}
            friend_image={friend_image}
            friend_name={friend_name}
            friend_book_date={friend_book_date}
            trip_id={trip_id}
          />
          <TinyTripReview userReview={userReview} />
          {main_video && shouldShowVideo && !userReview && !friend_id && playVideoBox()}
          {!userReview && !main_video && !friend_id && total_review_count > 0 && (
            <div
              style={{
                position: "absolute",
                bottom: 0,
                right: 0,
                margin: "8px",
                zIndex: 100,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: isMobile ? "12px" : "15px",
                textShadow: "0px 0px 2px rgba(0,0,0,0.5)",
              }}
            >
              <StarIcon style={{ fontSize: isMobile ? "14px" : "18px" }} />
              <span style={{ marginLeft: "3px" }}>
                {Math.max(4.5, (total_review_score / total_review_count).toFixed(1))} (
                {total_review_count * 4})
              </span>
            </div>
          )}
        </div>

        <div className="TinyTripSummary">
          <span
            style={{
              color: "#222222",
              fontWeight: "600",
              display: "flex",
              textAlign: "left",
              fontSize: isMobile ? "13px" : "15px",
            }}
          >
            {name}
          </span>
          <Stack
            className="TinyTripLocationLine"
            direction="row"
            spacing={0.6}
            alignItems="center"
            marginTop="-3px"
            style={{ whiteSpace: "nowrap" }}
          >
            <span style={{ color: "#333333", fontWeight: "400" }}>{country}</span>
            <FiberManualRecordIcon style={{ fontSize: "3px", color: "#333333" }} />
            {projectView ? (
              <span
                style={{
                  color: "#333333",
                  fontWeight: "400",
                }}
              >
                {durationLabel}
              </span>
            ) : isEvent ? (
              meeting_point && (
                <span
                  className="TinyTripMeetingPoint"
                  title={meeting_point}
                  style={{
                    color: "#333333",
                    fontWeight: "400",
                  }}
                >
                  {meeting_point}
                </span>
              )
            ) : (
              <span
                style={{
                  color: "#333333",
                  fontWeight: "400",
                }}
              >
                {skill_level}
              </span>
            )}
          </Stack>

          {!projectView && (
            <span style={{ color: "black", fontWeight: "500" }}>
              {is_dateless || !start_date
                ? t("create_new_date")
                : isEvent
                  ? [eventDateLabel, clubTime(start_time)].filter(Boolean).join(" · ")
                  : dateRangeFormattingShortYear(start_date, end_date)}
            </span>
          )}

          {is_dateless || price == null ? null : projectView ? (
            <span style={{ color: "#222222" }}>
              {special_discount_percentage > 0 ? (
                <span>
                  <span style={{ textDecoration: "line-through", color: "green" }}>
                    {formatCurrency(price)}
                  </span>
                  <span style={{ marginLeft: "3px" }}>
                    {formatCurrency(Math.ceil((price * (100 - special_discount_percentage)) / 100))}
                  </span>
                </span>
              ) : (
                <span>
                  {t("from_price", {
                    defaultValue: `From ${formatCurrency(min_price ?? price)}`,
                    price: formatCurrency(min_price ?? price),
                  })}
                </span>
              )}{" "}
              <span
                style={{
                  color: "#333333",
                  fontWeight: "400",
                }}
              >
                {t("per_pax", { defaultValue: "/pax" })}
              </span>
            </span>
          ) : (
            <span style={{ color: "#222222" }}>
              {/* Format the price using currency utils */}
              {special_discount_percentage > 0 ? (
                <span>
                  <span style={{ textDecoration: "line-through", color: "green" }}>
                    {formatCurrency(price)} {/* Format original price */}
                  </span>
                  <span style={{ marginLeft: "3px" }}>
                    {formatCurrency(Math.ceil((price * (100 - special_discount_percentage)) / 100))}{" "}
                    {/* Format discounted price */}
                  </span>
                </span>
              ) : earlyBirdSlotsRemaining <= 0 ? (
                formatCurrency(price) // Format regular price if no discount
              ) : (
                <span>
                  <span style={{ textDecoration: "line-through", color: "orange" }}>
                    {formatCurrency(Math.ceil(price * 1.4))} {/* Format adjusted price */}
                  </span>
                  <span style={{ marginLeft: "3px" }}>
                    {formatCurrency(min_price ?? price)} {/* Format minimum price */}
                  </span>
                </span>
              )}
              <span
                style={{
                  color: "#333333",
                  fontWeight: "400",
                }}
              >
                {t("per_pax", { defaultValue: "/pax" })}
              </span>
            </span>
          )}
        </div>
      </a>
    </div>
  );
};
